import {
  Button,
  Card,
  CardContent,
  Container,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { apiAccountAuthorizeGet } from "../api/account/authorize/get";
import { apiAccountStatusGet } from "../api/account/status/get";
import { FormEvent, useCallback, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ModalContext } from "../providers/ModalProvider";
import { CloseableModal } from "../components/common/CloseableModal";

const COLORS = {
  yellow: '#EBE32D',
  white: '#FFFFFF',
  grey: '#9AA0A6',
  lead: '#C9CDD1',
  line: 'rgba(255,255,255,.12)',
  cardBg: 'rgba(255,255,255,.035)',
};

export const LoginCompletePage = () => {
  const [urlSearchParams] = useSearchParams();
  const { createModal } = useContext(ModalContext);
  const theme = useTheme();

  const navigate = useNavigate();

  /**
   * Redirect the user to the homepage if they already have a sign-in session.
   */
  const redirectToCustomer = useCallback(async () => {
    const response = await apiAccountStatusGet();
    if (response && response.status === 200) {
      navigate('/products')
    }
  }, [navigate]);

  /**
   * Authorize the current sign-in session.
   *
   * @param event An HTML form event.
   * @returns A JSX element.
   */
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const token = urlSearchParams.get('token');
    const email = urlSearchParams.get('email');

    if (!token || !email) {
      navigate('/login');
      return;
    }

    const response = await apiAccountAuthorizeGet(token, email);

    if (response?.status === 200) {
      return redirectToCustomer();
    }

    return createModal(({ handleRemove }) => {
      return (
        <CloseableModal handleRemove={handleRemove}>
          <DialogTitle>
            Error
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Something went wrong, please try again.
            </DialogContentText>
          </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleRemove}>Close</Button>
        </DialogActions>
        </CloseableModal>
      );
    });
  };

  return (
    <Container sx={{ display: 'flex', justifyContent: 'center', pt: '12px' }}>
      <Card sx={{ mx: '12px', my: '24px', maxWidth: theme.breakpoints.values.sm, background: COLORS.cardBg, border: `1px solid ${COLORS.line}`, borderRadius: '14px' }}>
        <CardContent sx={{ flex: '1 0 auto' }}>
          <form onSubmit={onSubmit}>
            <Stack spacing={2} direction="column">
              <Typography variant="h6" sx={{ color: COLORS.white }}>
                Complete Sign-in
              </Typography>
              <Typography sx={{ color: COLORS.lead }}>
                Please confirm that you have authorized the request to sign in via the link sent to your email address.
                If you did not request this link, please close this window and ignore the message.
              </Typography>
              <Button
                type="submit"
                variant="contained"
                sx={{ backgroundColor: COLORS.yellow, color: '#1A1A1A', fontWeight: 600 }}
              >
                Authorize
              </Button>
              <Divider sx={{ borderColor: COLORS.line }} />
              <Typography sx={{ color: COLORS.grey }}>
                Your session will expire automatically within 30 minutes.
              </Typography>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
